/**
 * Unified Error Page Component
 *
 * A single, configurable error page that handles all HTTP error states.
 * Reduces code duplication across individual error components.
 *
 * @example
 * ```tsx
 * <ErrorPage
 *   status={404}
 *   title="Page Not Found"
 *   message="The page you're looking for doesn't exist."
 * />
 * ```
 */
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { Home, LogIn, RefreshCw } from 'lucide-react';
import { Button } from '@loader/ui';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@loader/ui';

import { errorConfigs, defaultErrorConfig } from './errorConfigs';

export interface ErrorPageProps {
  /**
   * HTTP status code or special error type (determines icon and default messages)
   */
  status?: number | string;
  /**
   * Custom title (overrides default for status)
   */
  title?: string;
  /**
   * Custom message (overrides default for status)
   */
  message?: string;
  /**
   * Error stack trace (shown in development only)
   */
  stack?: string;
  /**
   * Request ID for support reference
   */
  requestId?: string;
  /**
   * Full-screen mode (default) or inline
   */
  fullScreen?: boolean;
}

export function ErrorPage({
  status,
  title,
  message,
  stack,
  requestId,
  fullScreen = true,
}: ErrorPageProps) {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const isDevelopment = import.meta.env.DEV;

  // Get config for status code
  const config = status ? (errorConfigs[status] || defaultErrorConfig) : defaultErrorConfig;
  const Icon = config.icon;

  // Use custom values or defaults from config
  const displayTitle = title || config.title;
  const displayMessage = message || config.message;

  // Adjust for auth state
  const showSignIn = config.showSignIn && !isSignedIn;
  const showHome = config.showHome || (config.showSignIn && isSignedIn);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    // Hard navigation to break out of error boundary loops
    // (client-side navigate may re-trigger the same loader/error)
    window.location.href = '/workspace';
  };

  const handleSignIn = () => {
    navigate({ to: '/login', search: { from: window.location.pathname } });
  };

  const handleGoBack = () => {
    // Use history length check to avoid going back to the same error
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/workspace';
    }
  };

  const content = (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
          <Icon className="h-6 w-6 text-destructive" />
        </div>
        <CardTitle>{displayTitle}</CardTitle>
        <CardDescription>{displayMessage}</CardDescription>
        {requestId && (
          <p className="mt-2 text-xs text-muted-foreground">
            Request ID: {requestId}
          </p>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {config.showRetry && (
          <Button onClick={handleRetry} className="w-full">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        )}
        {showSignIn && (
          <Button onClick={handleSignIn} className="w-full">
            <LogIn className="mr-2 h-4 w-4" />
            Sign In
          </Button>
        )}
        {showHome && (
          <Button
            variant={config.showRetry || showSignIn ? 'outline' : 'default'}
            onClick={handleGoHome}
            className="w-full"
          >
            <Home className="mr-2 h-4 w-4" />
            Go to Workspace
          </Button>
        )}
        {config.showBack && (
          <Button variant="outline" onClick={handleGoBack} className="w-full">
            Go Back
          </Button>
        )}
        {isDevelopment && config.showStack && stack && (
          <div className="mt-4 rounded-md bg-muted p-3">
            <p className="mb-2 text-xs font-semibold text-muted-foreground">
              Development Stack Trace:
            </p>
            <pre className="max-h-48 overflow-auto text-xs text-muted-foreground">
              {stack}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (fullScreen) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        {content}
      </div>
    );
  }

  return content;
}
